(ns app.front.view.profile.licences
  (:require [app.domain.user.licence :as licence]
            [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.select :as ui-select]
            [app.front.ui.util :as uiu]
            [app.front.util :as futil :refer [for-frag for-frag-indexed]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-event-db ::set-user-licences
  (fn [db [_ licences]]
    (assoc db ::user-licences licences)))


(rf/reg-event-db ::set-adding-new
  (fn [db [_ adding?]]
    (assoc db ::adding-new? adding?)))


(rf/reg-event-fx ::save-new-licence
  (fn [_ [_ data]]
    {:comm/command {:command    :user/add-licence
                    :data       data
                    :on-success [::save-new-licence.success]}}))

(def success-notification [:notifications/add
                           :toast
                           :success
                           :user/changes-saved
                           :user/continue-editing])


(rf/reg-event-fx ::save-new-licence.success
  (fn [{:keys [db]} [_ _ new-licence]]
    {:db       (-> (update db ::user-licences conj new-licence)
                   (dissoc ::adding-new?))
     :dispatch success-notification}))


(rf/reg-event-fx ::save-licence-modification
  (fn [_ [_ id licence]]
    {:comm/command {:command    :user/modify-licence
                    :data       (assoc licence :licence-id id)
                    :on-success [::save-licence-modification.success]}}))

(rf/reg-event-fx ::remove-licence
  (fn [_ [_ id]]
    {:comm/command {:command    :user/remove-licence
                    :data       {:licence-id id}
                    :on-success [::save-licence-modification.success]}}))


(rf/reg-event-fx ::save-licence-modification.success
  (fn [{:keys [db]} [_ _ licence-map]]
    {:db       (assoc db ::user-licences (vals licence-map))
     :dispatch success-notification}))


(rf/reg-sub ::adding-new? :-> ::adding-new?)


(rf/reg-sub ::user-licences :-> ::user-licences)


(rf/reg-sub ::user-licences-sorted
  :<- [::user-licences]
  #(sort-by :db/id %))


(rf/reg-sub ::licence-config
  :<- [::front-config/config]
  :-> :user-licence-config)


(rf/reg-sub ::licence-config-for-type
  :<- [::licence-config]
  (fn [config [_ licence-type]]
    (get config licence-type {})))


(rf/reg-sub ::subtypes
  (fn [[_ licence-type]]
    (rf/subscribe [::licence-config-for-type licence-type]))
  :-> :subtypes)


(rf/reg-sub ::has-number?
  (fn [[_ licence-type]]
    (rf/subscribe [::licence-config-for-type licence-type]))
  (fn [config]
    (boolean (:number? config))))


(rf/reg-sub ::has-subtypes?
  (fn [[_ licence-type]]
    (rf/subscribe [::subtypes licence-type]))
  (fn [subtypes]
    (boolean (seq subtypes))))


(rf/reg-sub ::licence-types
  :<- [::licence-config]
  :<- [:tr]
  (fn [[config tr]]
    (->> config
         keys
         (sort-by #(tr [%])))))


(rf/reg-sub ::licence-types-for-new
  :<- [::user-licences]
  :<- [::licence-types]
  (fn [[licences types]]
    (let [existing-types (set (map :user-licence/type licences))]
      (->> types
           (remove #(contains? existing-types %))))))


(def field-wrapper
  (uiu/wrap-in
    :div.pr-0.w-full.mt-3.md:pr-4.md:w-1-3.xl:w-1-4))


(defn user-licence-fields [id tr licence-types current-data on-change-fn]
  (let [narrow?      (not (<sub [::media-query/breakpoint-matches? :md]))
        licence-type (:user-licence/type current-data)]
    [:<>
     [field-wrapper
      [ui-select/select {:id           (str id "-type")
                         :class-name   "full-width-select"
                         :values       licence-types
                         :val->str     #(tr [%])
                         :light        narrow?
                         :on-change    #(on-change-fn :user-licence/type %)
                         :value        licence-type
                         :allow-empty? false
                         :select-text  (tr [:profile-licences/type-placeholder])
                         :label-text   (tr [:profile-licences/type-label])}]]
     (when (<sub [::has-subtypes? licence-type])
       (let [subtypes (<sub [::subtypes licence-type])]
         [field-wrapper
          [ui-select/select {:id           (str id "-subtype")
                             :class-name   "full-width-select"
                             :values       subtypes
                             :val->str     #(tr [%])
                             :light        narrow?
                             :on-change    #(on-change-fn :user-licence/subtype %)
                             :value        (:user-licence/subtype current-data)
                             :allow-empty? false}]]))
     (when (<sub [::has-number? licence-type])
       [field-wrapper
        [ui/text-input {:id          (str id "-number")
                        :on-change   #(on-change-fn :user-licence/number (futil/tval %))
                        :light       narrow?
                        :label-text  (tr [:profile-licences/number-label])
                        :placeholder (tr [:profile-licences/number-label])
                        :invalid     (and (-> current-data :user-licence/number str/blank?)
                                          (:user-licence/number current-data))
                        :value       (:user-licence/number current-data)}]])]))


(def licence-row-wrapper
  (uiu/wrap-in :div.flex.flex-wrap.items-end.bg-ui-light-gray.md:bg-transparent.p-4.rounded.md:p-0.mt-3))


(defn new-licence-row [tr]
  (r/with-let [*data        (r/atom {})
               on-change-fn (fn [k v]
                              (swap! *data assoc k v))]
    (let [config (<sub [::front-config/config])]
      [licence-row-wrapper
       [user-licence-fields "new-user-licence" tr (<sub [::licence-types-for-new]) @*data on-change-fn]
       (cc/button {:kind       :tertiary
                   :class-name "mr-2 mt-3"
                   :size       :field
                   :on-click   #(>evt [::set-adding-new false])}
                  (tr [:cancel]))
       (cc/button {:kind       :primary
                   :class-name "mt-3"
                   :size       :field
                   :disabled   (not (licence/licence-valid? @*data config))
                   :on-click   #(>evt [::save-new-licence (licence/drop-extra-keys @*data config)])}
                  (tr [:save]))])))


(defn keep-modifiable
  [licence]
  (select-keys licence [:user-licence/type
                        :user-licence/number
                        :user-licence/subtype]))


(defn existing-licence-row [tr licence]
  (r/with-let [*data         (r/atom (keep-modifiable licence))
               on-change-fn  (fn [k v]
                               (swap! *data assoc k v))]
    (let [config      (<sub [::front-config/config])
          new-licence (licence/drop-extra-keys @*data config)
          modified?   (not= (keep-modifiable licence)
                            new-licence)
          valid?      (licence/licence-valid? new-licence config)]
      [licence-row-wrapper
       [user-licence-fields
        (str (:db/id licence) "-" (:entity/version licence))
        tr
        (<sub [::licence-types])
        @*data
        on-change-fn]
       (if modified?
         [:<>
          (cc/button {:kind       :primary
                      :size       :field
                      :class-name "mt-3 mr-2"
                      :disabled   (not valid?)
                      :on-click   #(>evt [::save-licence-modification (:db/id licence) new-licence])}
                     (tr [:save]))
          (cc/button {:kind       :secondary
                      :size       :field
                      :class-name "mt-3"
                      :on-click   #(reset! *data (keep-modifiable licence))}
                     (tr [:reset]))]
         (cc/button {:kind       :ghost
                     :size       :field
                     :class-name "mt-3"
                     :on-click   #(>evt [::remove-licence (:db/id licence)])}
                    (tr [:remove])))])))


(defn editor []
  (let [tr           (<sub [:tr])
        adding-new?  (<sub [::adding-new?])
        licences     (<sub [::user-licences-sorted])
        new-allowed? (seq (<sub [::licence-types-for-new]))]
    [eb/error-boundary
     [:div
      [:h4.mb-3.text-sm (tr [:profile-licences/title])]
      (for-frag [licence licences]
        ^{:key (:db/id licence)} [existing-licence-row tr licence])
      (when adding-new?
        [new-licence-row tr])
      (when (and new-allowed? (not adding-new?))
        [:div.mt-4
         (cc/button {:kind     :tertiary
                     :size     :field
                     :icon     i/plus
                     :on-click #(>evt [::set-adding-new true])}
                    (tr [:profile-licences/add-new]))])]]))


;; Functionality for user registration form


(rf/reg-event-db ::add-new-for-registration
  (fn [db _]
    (update db ::user-licences conj {})))


(rf/reg-event-db ::update-new-for-registration
  (fn [db [_ idx k v]]
    (update db ::user-licences (fn [licences]
                                 (let [[before after] (split-at idx licences)]
                                   (vec (concat before
                                                [(assoc (first after) k v)]
                                                (rest after))))))))


(rf/reg-event-db ::cancel-new-for-registration
  (fn [db [_ idx]]
    (update db ::user-licences (fn [licences]
                                 (let [[before after] (split-at idx licences)]
                                   (vec (concat before (rest after))))))))


(defn valid-licences-for-registration [db]
  (let [config         (front-config/db->config db)
        licence-valid? #(licence/licence-valid? % config)]
    (->> db
         ::user-licences
         (filter licence-valid?))))


(defn editor-for-registration []
  (let [tr           (<sub [:tr])
        licences     (<sub [::user-licences])
        new-allowed? (seq (<sub [::licence-types-for-new]))]
    [eb/error-boundary
     [:div.mb-12
      [:h4.mb-3.text-sm (tr [:profile-licences/title])]
      (for-frag-indexed [[i licence] licences]
        [licence-row-wrapper
         [user-licence-fields
          (str "new-user-licence-" i)
          tr
          (concat (<sub [::licence-types-for-new])
                  (when-let [t (:user-licence/type licence)]
                    [t]))
          licence
          (fn [k v]
            (>evt [::update-new-for-registration i k v]))]
         (cc/button {:kind       :tertiary
                     :class-name "mr-2 mt-3"
                     :size       :field
                     :on-click   #(>evt [::cancel-new-for-registration i])}
                    (tr [:cancel]))])
      (when new-allowed?
        [:div.mt-4
         (cc/button {:kind     :tertiary
                     :size     :field
                     :icon     i/plus
                     :on-click #(>evt [::add-new-for-registration])}
                    (tr [:profile-licences/add-new]))])]]))
